import { ConversationBuilderState } from "@/modules/conversation-builder";
import {
	Button,
	Form,
	Icon,
	Input,
	InputNumber,
	Upload,
	Modal,
	Progress,
	List,
	Select,
	Checkbox
} from "ant-design-vue";
import { WrappedFormUtils } from "ant-design-vue/types/form/form";
import { debounce } from "lodash";
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { getModule } from "vuex-module-decorators";
import { ConversationNode, ConversationNodeType, Dialog, DialogText } from "../../conversation-builder.model";
import ConversationImageEditor from "../conversation-image-editor/conversation-image-editor.vue";
import IConversationImageEditor from "../conversation-image-editor/conversation-image-editor";
import { ActionItem, fmtMsg } from "commonui";
import { getActualFileNameAndType } from "@/util/func";
import { EntityLengths } from "../../conversation-builder-util";
import {
	CommonLocale,
	ConversationBuilderLocale,
	ConversationTemplateLocale,
	FallbackLocale,
	OpenCloseLocale
} from "@/locales/localeid";
import {
	ConversationTemplateModel,
	ConversationTemplateService,
	FallbackTypeModel,
	MasterConversationType
} from "@/services/conversation-templates";
import { v4 } from "uuid";
import ConversationTemplateState from "@/modules/conversation-template";
import { InteractionStyleModel } from "@/services/conversation-builder";
import { validDialogInteractionStyles, validStartNodeInteractionStyles } from "@/util/constants";
@Component({
	components: {
		Form,
		FormItem: Form.Item,
		InputNumber,
		Input,
		TextArea: Input.TextArea,
		Upload,
		Button,
		Icon,
		ConversationImageEditor,
		ActionItem,
		Modal,
		Progress,
		List,
		ListItem: List.Item,
		Select,
		Option: Select.Option,
		Checkbox
	}
})
export default class DialogProperties extends Vue {
	@Prop() node!: ConversationNode<Dialog>;
	public form!: WrappedFormUtils;

	private emitEvent: boolean = true;

	public conversationStore = getModule(ConversationBuilderState);
	public templateState = getModule(ConversationTemplateState);
	public maxNameLength = EntityLengths.dialog.name.max;
	public maxTextLength = EntityLengths.dialog.description.max;
	public minRetries = EntityLengths.dialog.retries.min;
	public maxRetries = EntityLengths.dialog.retries.max;
	public format = fmtMsg;
	public locale = FallbackLocale;
	public showAddEditModal = false;
	public saving = false;
	public commonLocale = CommonLocale;
	public templateLocale = ConversationTemplateLocale;
	public conversationLocale = ConversationBuilderLocale;
	public fallbackTypes: FallbackTypeModel[] = [];
	public fallbackList: FallbackTypeModel[] = [];
	public selectedTextType: MasterConversationType = MasterConversationType.Silent;
	public textType = MasterConversationType;
	public loading = false;
	public fallbacks: ConversationTemplateModel[] = [];
	private templateService = ConversationTemplateService();
	public selectedFilter = 0;
	public selectedTag = "";
	public dialogAlternateText = "dialogAlternateText";

	$refs!: {
		imageEditor: IConversationImageEditor;
	};

	created() {
		this.form = this.$form.createForm(this, {
			name: "dialog-properties",
			onValuesChange: debounce(this.onValuesChange, 100)
		});

		this.getFallbackTemplates();

		if (!this.templateState.conversationTags.length) {
			this.templateState.fetchConversationTags();
		}
	}

	mounted() {
		this.setValues(false);
	}

	@Watch("conversationStore.selectedNode")
	onChange() {
		this.setValues(false);
	}

	private setValues(emitEvent: boolean) {
		this.minRetries = EntityLengths.dialog.retries.min;
		this.emitEvent = emitEvent;
		const {
			data: { name, retryCount, text, interactionStyleId, isConclusion }
		} = this.node;

		this.form.setFieldsValue({
			name,
			retryCount,
			text,
			interactionStyleId,
			isConclusion
		});
		const filedsToValidate: string[] = [];

		if (name && name.length) {
			filedsToValidate.push(Object.keys({ name })[0]);
		}

		if (!retryCount) {
			filedsToValidate.push(Object.keys({ retryCount })[0]);
		}

		if (text && text.length) {
			filedsToValidate.push(Object.keys({ text })[0]);
		}
		this.setRetriesMinValue();

		this.form.validateFields(filedsToValidate);
	}

	private onValuesChange(props: any, fields: Partial<Dialog>) {
		if (!this.emitEvent) {
			this.emitEvent = true;
			return false;
		}

		let retries = this.node.data.retryCount;
		let silentText = this.node.data.silentDialogText;
		let fallBackText = this.node.data.fallbackDialogText;

		// If retry count is changed, only then handle decimal values.
		if (fields.retryCount || fields.retryCount === 0) {
			const { min, max } = EntityLengths.dialog.retries;
			retries = fields.retryCount ? Math.ceil(fields.retryCount) : min;
			retries = retries > max ? max : retries;
			retries = retries < min ? min : retries;
		}

		if (typeof fields.isConclusion === "boolean") {
			silentText = [];
			fallBackText = [];
		}

		this.conversationStore.updateNodeData<Dialog>({
			id: this.node.id,
			data: { ...fields, retryCount: retries, silentDialogText: silentText, fallbackDialogText: fallBackText }
		});
	}

	get isStartNode() {
		if (typeof this.node.data.isHead == "boolean" && this.node.data.isHead) {
			return true;
		}
		return false;
	}

	get isConclusion() {
		return this.node.data.isConclusion;
	}

	get imageUrl() {
		return this.node.data.imageUrl as string;
	}

	get nodePageId() {
		return this.node.data.pageId;
	}

	async downloadImage() {
		this.conversationStore.downloadFile(this.imageUrl);
	}

	public get interactionStyles(): InteractionStyleModel[] {
		if (this.node.type === ConversationNodeType.Conversation) {
			return validStartNodeInteractionStyles;
		} else {
			return validDialogInteractionStyles;
		}
	}

	public removeImage(closeEditor: () => void) {
		const remove = async () => {
			closeEditor && closeEditor();
			await this.conversationStore.deleteImage(this.node);
		};

		Modal.confirm({
			title: fmtMsg(this.conversationLocale.ConversationDialogImageRemoveMsg),
			onOk: remove
		});
	}

	get getImageName() {
		if (this.imageUrl) {
			return getActualFileNameAndType(this.imageUrl);
		}
	}

	isImageUploadInProgress() {
		return this.conversationStore.fileUploads.find((f) => f.nodeId === this.node.id);
	}

	get imageUploadProgress() {
		const uploadInfo = this.isImageUploadInProgress();
		if (uploadInfo) {
			return Math.floor((uploadInfo.uploaded * 100) / uploadInfo.size);
		}
	}

	public openImageEditor() {
		this.$refs.imageEditor.startEditing();
	}

	public deleteDialogText(textType: MasterConversationType, id: string, value: string) {
		const updatedTexts = (
			(textType == MasterConversationType.Silent
				? this.node.data.silentDialogText
				: this.node.data.fallbackDialogText) ?? ([] as DialogText[])
		)
			.filter((f) => f.id !== id)
			.map((m, index) => {
				m.order = index + 1;
				return m;
			});
		Modal.confirm({
			title: fmtMsg(this.locale.FallbackDeleteAlternateText),
			onOk: () => {
				this.updateNode(textType, updatedTexts);
			}
		});
	}

	public isSilentDisabled() {
		const { silentDialogText, retryCount, isConclusion } = this.node.data;
		return isConclusion || (silentDialogText ? silentDialogText.length >= retryCount : !retryCount);
	}

	public isFallbackDisabled() {
		const { fallbackDialogText, retryCount, isConclusion } = this.node.data;
		return isConclusion || (fallbackDialogText ? fallbackDialogText.length >= retryCount : !retryCount);
	}

	public openDialogTextModal(textType: MasterConversationType) {
		if (
			(textType == MasterConversationType.Silent && this.isSilentDisabled()) ||
			(textType == MasterConversationType.Regular && this.isFallbackDisabled())
		) {
			return false;
		}
		this.selectedTextType = textType;
		this.selectedFilter = 0; // set custom by default
		this.showAddEditModal = true;
		this.fallbackTypes = [
			{
				id: 0,
				display: this.format(this.locale.FallbackCustom)
			} as FallbackTypeModel
		];

		this.fallbacks
			.filter((f) => f.type == textType)
			.map((item, index) => {
				this.fallbackTypes.push({
					id: item.id,
					display: item.text
				} as FallbackTypeModel);
			});

		this.fallbackList = this.fallbackTypes;
	}

	onAddEditModalCancel() {
		this.showAddEditModal = false;
	}

	onAddEdit() {
		const self = this;
		this.form.validateFields([this.dialogAlternateText], (err, values) => {
			if (!err) {
				self.saving = true;

				let alternateText: DialogText[] = [];

				let maxOrder = 0;
				if (self.selectedTextType == MasterConversationType.Silent) {
					maxOrder = self.node.data.silentDialogText?.length ?? 0;
					const silentText = self.node.data.silentDialogText ?? [];
					alternateText = [
						...silentText,
						{
							id: v4(),
							key: 0,
							value: values.dialogAlternateText,
							order: maxOrder + 1,
							dialogType: MasterConversationType.Silent
						} as DialogText
					];
				} else {
					maxOrder = self.node.data.fallbackDialogText?.length ?? 0;
					const fallbackText = self.node.data.fallbackDialogText ?? [];

					alternateText = [
						...fallbackText,
						{
							id: v4(),
							key: 0,
							value: values.dialogAlternateText,
							order: maxOrder + 1,
							dialogType: MasterConversationType.Regular
						} as DialogText
					];
				}
				this.updateNode(this.selectedTextType, alternateText);
				self.saving = false;
				self.showAddEditModal = false;
			}
		});
	}

	updateNode(textType: MasterConversationType, alternateText: DialogText[]) {
		if (textType == MasterConversationType.Silent) {
			this.conversationStore.updateNodeData<Dialog>({
				id: this.node.id,
				data: { ...this.node.data, silentDialogText: alternateText }
			});
		} else {
			this.conversationStore.updateNodeData<Dialog>({
				id: this.node.id,
				data: { ...this.node.data, fallbackDialogText: alternateText }
			});
		}
		setTimeout(() => {
			this.setRetriesMinValue();
		}, 500);
	}

	remainingCount(): number {
		var text = this.form.getFieldValue("dialogAlternateText");
		const calculateRemainingLength = (value: string) => {
			if (!value) {
				return this.maxTextLength;
			}

			if (this.maxTextLength - value.length >= 0) {
				return this.maxTextLength - value.length;
			} else {
				return 0;
			}
		};

		return calculateRemainingLength(text);
	}

	requiredTextValidator(rule: any, value: string, callback: (msg?: string) => void) {
		value = (value || "").trim();

		if (!value) {
			callback(this.format(this.templateLocale.TemplateValidationText));
			return;
		}

		callback();
	}

	uniqueTextValidator(rule: any, value: string, callback: (msg?: string) => void) {
		const list =
			(this.selectedTextType == MasterConversationType.Silent
				? this.node.data.silentDialogText
				: this.node.data.fallbackDialogText) ?? ([] as DialogText[]);

		const index = list.findIndex((fallback) => fallback.value.trim().toLowerCase() === value.trim().toLowerCase());

		if (index >= 0) {
			callback(fmtMsg(this.templateLocale.TemplateValidationUniqueText));
			return;
		}

		callback();
	}

	setRetriesMinValue() {
		const baseMin = EntityLengths.dialog.retries.min;
		const silentCount = this.node.data.silentDialogText?.length ?? 0;
		const fallbackCount = this.node.data.fallbackDialogText?.length ?? 0;

		if (silentCount > baseMin || fallbackCount > baseMin) {
			if (silentCount > fallbackCount) {
				this.minRetries = silentCount;
			} else {
				this.minRetries = fallbackCount;
			}
		} else {
			this.minRetries = EntityLengths.dialog.retries.min;
		}
	}

	getFallbackTemplates() {
		this.loading = true;

		this.templateService
			.getTemplates({ types: [MasterConversationType.Regular, MasterConversationType.Silent] })
			.then((fallbacks) => {
				this.fallbacks = fallbacks;
				this.loading = false;
			})
			.catch(() => {
				this.loading = false;
			});
	}

	onFallbackTypeChange(selectedItem) {
		this.selectedFilter = selectedItem;
		let text = "";

		if (selectedItem > 0) {
			const item = this.fallbacks.filter((a) => a.id == selectedItem);
			if (item && item.length > 0) {
				text = item[0].text;
			}
		}
		this.form.setFieldsValue({
			dialogAlternateText: text
		});
	}

	onAddTag() {
		const selectedTag = this.templateState.conversationTags.find(
			(tag) => tag.id === ((this.selectedTag as unknown) as number)
		);

		if (selectedTag) {
			let text: string = this.form.getFieldValue("dialogAlternateText") || "";
			let tag = `{${selectedTag.value}}`;

			if (text && !text.endsWith(" ")) {
				tag = ` ${tag}`;
			}

			text += tag;

			this.form.setFieldsValue({ dialogAlternateText: text.substr(0, this.maxTextLength) });
			this.form.validateFields(["dialogAlternateText"]);
		}
	}

	// Checks for invalid conversation tag, returns true if valid, false otherwise.
	isTagValid(regex, text) {
		const result = regex.exec(text);

		if (result && result[0]) {
			var matchText = result[0];

			if (this.templateState.conversationTags.some((tag) => `{${tag.value}}` === matchText)) {
				return this.isTagValid(regex, text); // recursively validate all tags.
			} else {
				return false;
			}
		}

		return true;
	}

	// Validates tag and its count.
	tagValidator(rule: any, value: string, callback: (msg?: string) => void) {
		var regex = /\{.*?\}/g;
		const text = this.form.getFieldValue("dialogAlternateText");
		const isTagValid = this.isTagValid(regex, text);

		if (!isTagValid) {
			callback(this.format(OpenCloseLocale.OpenCloseValidationTag));
			return;
		}

		const isTagCountValid = this.isTagCountValid(text);

		if (!isTagCountValid) {
			callback(this.format(OpenCloseLocale.OpenCloseValidationTag));
			return;
		}

		callback();
	}

	// Checks if count of opening tags '{' match is equal to closing tags '}', true if matched, false otherwise.
	isTagCountValid(text) {
		const openTagRegex = /\{/g;
		const closeTagRegex = /\}/g;
		let openCount = 0;
		let closeCount = 0;
		let openResult;
		let closeResult;

		while ((openResult = openTagRegex.exec(text))) {
			openCount++;
		}

		while ((closeResult = closeTagRegex.exec(text))) {
			closeCount++;
		}

		return openCount === closeCount;
	}

	// onFallbackTypeSearch(e) {
	// 	this.fallbackList = this.fallbackTypes.filter((f) => f.display.includes(e.target.value.trim()));
	// }
}
